import {match} from 'ts-pattern';

import {RefObject, useState} from 'react';

import {isNotNil} from 'ramda';

import {getSafely} from 'shared/web';

import {TimeType} from '../../../types/TimeType';

interface UseTimePickerFocusManagementProps {
  visibleOptions: TimeType[];
  value: TimeType | null;
  scrollTargetTime: TimeType;
  isOptionDisabled: (option: TimeType) => boolean;
  inputRef: RefObject<HTMLInputElement | null>;
}

/**
 * Focus model of the TimePicker dropdown: the input keeps real DOM focus while the
 * list is navigated through a virtual highlight (so typing keeps working), and the
 * chips panel takes real roving focus — mirroring DatePicker's zone behavior with
 * only two zones.
 */
export function useTimePickerFocusManagement(props: UseTimePickerFocusManagementProps) {
  const [focusedOptionIndex, setFocusedOptionIndex] = useState<number | null>(null);
  const [areChipsFocused, setAreChipsFocused] = useState(false);
  const [focusedChipIndex, setFocusedChipIndex] = useState(-1);

  const getInitialOptionIndex = () => {
    const valueIndex = isNotNil(props.value) ? props.visibleOptions.indexOf(props.value) : -1;

    return match(valueIndex)
      .with(-1, () => Math.max(props.visibleOptions.indexOf(props.scrollTargetTime), 0))
      .otherwise((index) => index);
  };

  const getEnabledOptionIndex = (fromIndex: number, direction: 1 | -1, includeStart: boolean) => {
    const optionsCount = props.visibleOptions.length;
    const startOffset = includeStart ? 0 : 1;

    for (let offset = startOffset; offset <= optionsCount; offset++) {
      const index = (fromIndex + direction * offset + optionsCount * offset) % optionsCount;
      const option = getSafely(props.visibleOptions, index);
      if (isNotNil(option) && !props.isOptionDisabled(option)) {
        return index;
      }
    }
    return null;
  };

  const resetFocus = () => {
    setFocusedOptionIndex(null);
    setAreChipsFocused(false);
    setFocusedChipIndex(-1);
  };

  const moveOptionFocus = (direction: 1 | -1) => {
    if (props.visibleOptions.length === 0) {
      return;
    }
    setAreChipsFocused(false);
    setFocusedChipIndex(-1);
    setFocusedOptionIndex((current) =>
      match(current)
        // The first arrow press enters the list at the opening row (or the nearest
        // enabled row past a min/max bound).
        .with(null, () => getEnabledOptionIndex(getInitialOptionIndex(), direction, true))
        .otherwise((index) => getEnabledOptionIndex(index, direction, false))
    );
  };

  const focusChips = () => {
    setAreChipsFocused(true);
    setFocusedChipIndex(0);
    setFocusedOptionIndex(null);
  };

  const focusList = () => {
    setFocusedOptionIndex(getEnabledOptionIndex(getInitialOptionIndex(), 1, true));
    setAreChipsFocused(false);
    setFocusedChipIndex(-1);
    // The list highlight is virtual — keystrokes must land on the input again.
    props.inputRef.current?.focus();
  };

  const chipFocusProps = {
    isFocused: areChipsFocused,
    focusedIndex: focusedChipIndex,
    onFocusChange: (index: number) => {
      setAreChipsFocused(true);
      setFocusedChipIndex(index);
    },
    onBlur: () => {
      setAreChipsFocused(false);
      setFocusedChipIndex(-1);
      props.inputRef.current?.focus();
    },
  };

  return {
    focusedOptionIndex,
    areChipsFocused,
    chipFocusProps,
    moveOptionFocus,
    focusChips,
    focusList,
    resetFocus,
  };
}
